import CalendarDays from 'lucide-react/dist/esm/icons/calendar-days';
import Clock from 'lucide-react/dist/esm/icons/clock';
import Users from 'lucide-react/dist/esm/icons/users';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { getEventCardDateLabel, getEventCardGuestLabel, resolvedEventBookingType, TDR } from 'tdr-common';
import { getEventCdnImageUri } from '../../helpers/getCDNURI';
import { EventBookNowButton } from '../events/EventBookNowButton';
import { EventBookNowOnResultsButton } from '../events/EventBookNowOnResultsButton';
import { formatBetweenTimes, getEventImagePath, getNonRecurringDatesDetailLines } from '../events/EventCard';
import { AvailabilityGuardContext } from '../../routes/AvailabilityGuard';
import { analytics } from '../../helpers/analytics';
import { RESULT_CLICKED } from '../../helpers/analytics/constants';

const CARD_IMAGE_HEIGHT = 240;

type InventoryEventCardProps = {
	event: TDR.Event;
};

export const InventoryEventCard = ({ event }: InventoryEventCardProps) => {
	const { t } = useTranslation();
	const [showDetails, setShowDetails] = useState(false);
	const { restaurant, tables, searchFilters, updateSearchParams } =
		useOutletContext<AvailabilityGuardContext>();

	const eventInfo = event.displays[0];
	const dateLabel = getEventCardDateLabel(event);
	const timeInfo = event.condition?.value?.betweenTimes
		? formatBetweenTimes(event.condition.value.betweenTimes)
		: null;
	const guestLabel = getEventCardGuestLabel(event);
	const nonRecurringDateLines = getNonRecurringDatesDetailLines(event);
	const hasExpandableDetails =
		(nonRecurringDateLines?.length ?? 0) > 0 || (eventInfo.description?.length ?? 0) > 0;

	const imageSrc = getEventCdnImageUri(getEventImagePath(event.id), {
		width: 359,
		height: CARD_IMAGE_HEIGHT,
		fit: 'crop',
		dpr: 2
	});

	const bookNowClassName = 'font-poppins text-sm font-semibold text-zinc-200 hover:text-white';
	const bookNowDisabledClassName = 'font-poppins text-sm font-semibold text-zinc-400';
	const handleBookNowClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		analytics.track(RESULT_CLICKED, { resultType: 'event' });
	};

	const bookNowControl = resolvedEventBookingType(event) === 'table' ? (
		<EventBookNowButton
			event={event}
			restaurantSlug={restaurant.slug}
			tables={tables}
			restaurantId={restaurant.id}
			timezone={restaurant.timezone ?? null}
			className={bookNowClassName}
			disabledClassName={bookNowDisabledClassName}
			onClick={handleBookNowClick}
		/>
	) : (
		<EventBookNowOnResultsButton
			event={event}
			tables={tables}
			restaurantId={restaurant.id}
			timezone={restaurant.timezone ?? null}
			currentFilters={searchFilters}
			updateSearchParams={updateSearchParams}
			className={bookNowClassName}
			disabledClassName={bookNowDisabledClassName}
			onClick={handleBookNowClick}
		/>
	);

	return (
		<div
			data-testid="inventory-event-card"
			className="bg-tablz-med-gray-3 hover:bg-tablz-med-gray-6 p-1 rounded-2xl border border-tablz-med-gray-4 shadow-[0px_1px_2px_#0000000D] max-w-[400px] sm:max-w-[400px] sm:min-w-[min(370px,90vw)] w-full h-[388px] relative flex flex-col overflow-hidden"
		>
			<motion.div
				className="w-full shrink-0 overflow-hidden rounded-xl"
				initial={false}
				animate={{
					height: showDetails && hasExpandableDetails ? 0 : CARD_IMAGE_HEIGHT,
					opacity: showDetails && hasExpandableDetails ? 0 : 1
				}}
				transition={{ duration: 0.2, ease: 'easeInOut' }}
			>
				<img
					className="w-full h-[240px] object-cover rounded-xl"
					src={imageSrc}
					alt={eventInfo.title}
				/>
			</motion.div>

			<div className="w-full shrink-0 pt-2 px-3">
				<p className="font-lora text-xl text-white mb-1 truncate">{eventInfo.title}</p>
				{(dateLabel || timeInfo || guestLabel) && (
					<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-poppins font-semibold uppercase tracking-wide text-tablz-text-gray-2">
						{dateLabel && (
							<span className="inline-flex items-center gap-1">
								<CalendarDays size={14} />
								{dateLabel}
							</span>
						)}
						{timeInfo && (
							<span className="inline-flex items-center gap-1">
								<Clock size={14} />
								{timeInfo}
							</span>
						)}
						{guestLabel && (
							<span className="inline-flex items-center gap-1">
								<Users size={14} />
								{guestLabel}
							</span>
						)}
					</div>
				)}
			</div>

			{hasExpandableDetails && (
				<motion.div
					className="min-h-0 flex-1 overflow-hidden px-3"
					initial={false}
					animate={{
						height: showDetails ? 'auto' : 0,
						opacity: showDetails ? 1 : 0
					}}
					transition={{ duration: 0.2, ease: 'easeInOut' }}
				>
					<div className="flex flex-col gap-2 overflow-y-auto max-h-full pb-2 text-sm font-poppins text-tablz-text-gray-2">
						{nonRecurringDateLines?.length ? (
							<div className="rounded-lg border border-white/10 bg-white/[0.06] px-3 py-2">
								<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-white/50">Dates</p>
								<p className="text-sm text-white/90">{nonRecurringDateLines.join(' | ')}</p>
							</div>
						) : null}
						{eventInfo.description ? (
							<p className="text-sm leading-relaxed text-white/90">{eventInfo.description}</p>
						) : null}
					</div>
				</motion.div>
			)}

			<div className="bg-tablz-med-gray-1 shrink-0 flex items-center justify-between p-3 rounded-2xl text-sm min-h-[44px] mt-auto">
				<div className="flex items-center gap-1 min-h-[20px]">{bookNowControl}</div>
				{hasExpandableDetails ? (
					<button
						type="button"
						className="font-poppins font-semibold text-[color:var(--zinc-200,#e4e4e7)] cursor-pointer"
						onClick={() => setShowDetails((prev) => !prev)}
						data-testid="event-see-details-btn"
						aria-expanded={showDetails}
					>
						{showDetails ? t('actions.close') : t('inventory.see_details')}
					</button>
				) : null}
			</div>
		</div>
	);
};
